import { FunctionComponent, useEffect, useState, memo } from 'react';

import MyScholasticIcon from '../../../assets/logos/scholastic-icon.png';
import { ReactComponent as GoogleLogo } from '../../../assets/logos/google-g-icon.svg';
import { usePlatform } from '../../../providers/PlatformProvider/PlatformProvider';
import { logError } from '../../../utils/api/api';
import { useSettings } from '../../../providers/SettingsProvider/SettingsProvider';
import { useGoogleSignIn } from '../../../behaviors/api-hooks/use-google-sign-in/use-google-sign-in';
import { useCleverSignIn } from '../../../behaviors/api-hooks/use-clever-sign-in/use-clever-sign-in';
import useNavigateWithSearchParams from '../../../behaviors/use-navigate-with-search-params/use-navigate-with-search-params';
import useUserContextApi from '../../../behaviors/api-hooks/use-user-context-api/use-user-context-api';
import useClasses from '../../../behaviors/use-classes/use-classes';
import { useWindowQuery } from '../../../providers/ResponsivityProvider/ResponsivityProvider';
import { Button, Modal } from '../../../components';

import './SignInButtons.scss';

export interface SignInButtonsProps {
  showMyScholastic?: boolean;
  showGoogle?: boolean;
  showClever?: boolean;
  onCleverClick?: () => void;
  onGoogleClick?: () => void;
  isOpen?: boolean;
  onModalClose?: () => void;
}

const baseClass = 'customer-portal-sign-in-buttons';

const SignInButtons: FunctionComponent<SignInButtonsProps> = ({
  showMyScholastic = false,
  showGoogle = false,
  showClever = false,
  onGoogleClick,
  onCleverClick,
  isOpen = false,
  onModalClose,
}: SignInButtonsProps) => {
  const { navigateWithSearchParams } = useNavigateWithSearchParams();
  const {
    cleverLoginEnabled,
    googleLoginEnabled,
    myScholasticLoginEnabled,
    myScholasticLoginUrl,
  } = useSettings();
  const { track } = usePlatform();

  const { loginUser } = useUserContextApi({});
  const signInWithGoogle = useGoogleSignIn({
    googleLoginEnabled: googleLoginEnabled && showGoogle,
  });
  const signInWithClever = useCleverSignIn({
    cleverLoginEnabled: cleverLoginEnabled && showClever,
  });

  const [mySchlOpen, setMySchlOpen] = useState(isOpen);

  const { responsiveClassName } = useWindowQuery({ className: baseClass });
  const classes = useClasses({ baseClass }, [responsiveClassName]);

  const mySchlEnabled = !!myScholasticLoginUrl && !!myScholasticLoginEnabled;
  const mySchlIFrameUrl = `${myScholasticLoginUrl}?ref=sdm`;

  useEffect(() => {
    const myScholasticHandler = async (event: MessageEvent) => {
      if (event && event?.origin === myScholasticLoginUrl) {
        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          const status = eventData?.status;
          const data = eventData?.data;

          if (status === 'CLOSE') {
            setMySchlOpen(false);
            onModalClose?.();
          } else if (status === 'SUCCESS' && data !== null) {
            await loginUser();
            onModalClose?.();
            navigateWithSearchParams('/');
          } else if (status === 'REDIRECT') {
            // redirect to register page using URL from data field
            // Persist app code in url for redirect to keep app flow
            const appCode = new URLSearchParams(window.location.search)?.get('app');
            if (data.sdm) {
              const redirectUrl = appCode ? `${data.sdm}?app=${appCode}` : data.sdm;
              window.location.assign(redirectUrl);
            }
          } else if (status === 'RESET') {
            // Leave modal open so user can make further decisions
            track('forget-password', {});
          } else if (status) {
            throw Error('Unknown status from MyScholastic login.');
          }
        } catch (e) {
          setMySchlOpen(false);
          onModalClose?.();

          logError('Error handling MyScholastic event');
          logError(JSON.stringify(e));
        }
      }
    };

    if (mySchlOpen) {
      window.addEventListener('message', myScholasticHandler, true);
    }

    return () => {
      window.document.body.style.paddingRight = '0';
      window.removeEventListener('message', myScholasticHandler, true);
    };
  }, [loginUser, mySchlOpen, myScholasticLoginUrl, navigateWithSearchParams, onModalClose, track]);

  return (
    <div className={classes} data-testid="signInButtons">
      {mySchlEnabled && showMyScholastic && (
        <Button
          className={`${baseClass}__button`}
          color="light"
          onClick={() => {
            setMySchlOpen(true);
            track('myscholastic-signin-button', {});
          }}
          data-testid="myScholasticLogin"
        >
          <img src={MyScholasticIcon} title="Sign in with My Scholastic" alt="My Scholastic" />{' '}
          <p className={`${baseClass}__button-text`}>MyScholastic</p>
        </Button>
      )}
      {showGoogle && (
        <Button
          className={`${baseClass}__button`}
          color="light"
          onClick={() => {
            onGoogleClick?.();
            signInWithGoogle();
            track('google-signin-button', {});
          }}
        >
          <GoogleLogo title="Sign in with Google Classroom" />{' '}
          <p className={`${baseClass}__button-text`}>Google Classroom</p>
        </Button>
      )}
      {showClever && (
        <Button
          className={`${baseClass}__button`}
          color="light"
          onClick={() => {
            onCleverClick?.();
            signInWithClever();
            track('clever-signin-button', {});
          }}
          title="Sign in with Clever"
        >
          <div className={`${baseClass}__clever-logo`}>
            <span>C</span>
          </div>
          <p className={`${baseClass}__button-text`}>Clever</p>
        </Button>
      )}
      {mySchlEnabled && (
        <MyScholasticModal mySchlIFrameUrl={mySchlIFrameUrl} mySchlOpen={mySchlOpen} />
      )}
    </div>
  );
};

const MyScholasticModal = memo(
  ({ mySchlOpen, mySchlIFrameUrl }: { mySchlOpen: boolean; mySchlIFrameUrl: string }) => {
    return (
      <Modal
        className={`${baseClass}__modal`}
        isOpen={mySchlOpen}
        onExit={() => {
          document.body.className = document.body.className.replace(/^vol-body--modal-open$/, '');
        }}
      >
        <iframe src={mySchlIFrameUrl} title="MyScholastic Login" />
      </Modal>
    );
  },
  (prev, curr) => prev.mySchlOpen === curr.mySchlOpen,
);

export default SignInButtons;
